import React, { useEffect } from 'react';

interface HeadContentData {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonicalUrl?: string;
  favicon?: string;
  appleTouchIcon?: string;
  cssLinks?: Array<{
    href: string;
    rel?: string;
    type?: string;
    media?: string;
  }>;
  inlineStyles?: string;
  scriptLinks?: Array<{
    src: string;
    async?: boolean;
    defer?: boolean;
    crossorigin?: string;
  }>;
  inlineScripts?: string;
  structuredData?: Array<Record<string, any>>;
  customMeta?: Array<{
    name?: string;
    property?: string;
    content: string;
  }>;
}

interface HeadContentComponentProps {
  component: {
    id: string;
    type: 'head-content';
    data: Partial<HeadContentData>;
    order: number;
    visible: boolean;
    createdAt?: string;
    updatedAt?: string;
  };
}

export const HeadContentComponent: React.FC<HeadContentComponentProps> = ({ component }) => {
  const { data = {} } = component;

  console.log('🔍 HeadContentComponent rendering with data:', {
    hasTitle: !!data.title,
    hasDescription: !!data.description,
    hasKeywords: !!data.keywords,
  });

  // DIRECT DOM MANIPULATION - Bypass React Helmet's issues
  useEffect(() => {
    console.log('💉 Directly injecting meta tags into DOM');

    // Helper to set or update a meta tag
    const setMetaTag = (selector: string, attributes: Record<string, string>) => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        document.head.appendChild(tag);
      }
      Object.entries(attributes).forEach(([key, value]) => {
        if (value) tag.setAttribute(key, value);
      });
    };

    // Helper to set or update a link tag
    const setLinkTag = (selector: string, attributes: Record<string, string>) => {
      let tag = document.querySelector(selector) as HTMLLinkElement;
      if (!tag) {
        tag = document.createElement('link');
        document.head.appendChild(tag);
      }
      Object.entries(attributes).forEach(([key, value]) => {
        if (value) tag.setAttribute(key, value);
      });
    };

    // Title
    if (data.title) {
      document.title = data.title;
    }

    // Basic meta tags
    if (data.description) {
      setMetaTag('meta[name="description"]', { name: 'description', content: data.description });
    }
    if (data.keywords) {
      setMetaTag('meta[name="keywords"]', { name: 'keywords', content: data.keywords });
    }

    // Open Graph tags
    if (data.ogTitle) {
      setMetaTag('meta[property="og:title"]', { property: 'og:title', content: data.ogTitle });
    }
    if (data.ogDescription) {
      setMetaTag('meta[property="og:description"]', {
        property: 'og:description',
        content: data.ogDescription,
      });
    }
    if (data.ogImage) {
      setMetaTag('meta[property="og:image"]', { property: 'og:image', content: data.ogImage });
    }
    if (data.ogType) {
      setMetaTag('meta[property="og:type"]', { property: 'og:type', content: data.ogType });
    }

    // Twitter Card tags
    if (data.twitterCard) {
      setMetaTag('meta[name="twitter:card"]', { name: 'twitter:card', content: data.twitterCard });
    }
    if (data.twitterTitle) {
      setMetaTag('meta[name="twitter:title"]', {
        name: 'twitter:title',
        content: data.twitterTitle,
      });
    }
    if (data.twitterDescription) {
      setMetaTag('meta[name="twitter:description"]', {
        name: 'twitter:description',
        content: data.twitterDescription,
      });
    }
    if (data.twitterImage) {
      setMetaTag('meta[name="twitter:image"]', {
        name: 'twitter:image',
        content: data.twitterImage,
      });
    }

    // Canonical URL
    if (data.canonicalUrl) {
      setLinkTag('link[rel="canonical"]', { rel: 'canonical', href: data.canonicalUrl });
    }

    // Favicon
    if (data.favicon) {
      setLinkTag('link[rel="icon"]', { rel: 'icon', href: data.favicon });
    }
    if (data.appleTouchIcon) {
      setLinkTag('link[rel="apple-touch-icon"]', {
        rel: 'apple-touch-icon',
        href: data.appleTouchIcon,
      });
    }

    // Custom meta tags
    data.customMeta?.forEach(meta => {
      if (meta.name) {
        setMetaTag(`meta[name="${meta.name}"]`, { name: meta.name, content: meta.content });
      } else if (meta.property) {
        setMetaTag(`meta[property="${meta.property}"]`, {
          property: meta.property,
          content: meta.content,
        });
      }
    });

    console.log('✅ Meta tags injected directly into DOM');
  }, [data]);

  // Component doesn't render anything - all meta tags are injected directly into DOM via useEffect
  return null;
};
